import React, { useState, useEffect } from 'react';
import { Github, Linkedin, Mail, Code, Terminal, Shield, ExternalLink, ArrowRight, Download, Menu, X } from 'lucide-react';

const ModernPortfolio = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentText, setCurrentText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const texts = [
    'Web Developer',
    'Full Stack Engineer', 
    'React Specialist',
    'Node.js Developer'
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const currentFullText = texts[textIndex];
      
      if (currentText.length < currentFullText.length) {
        const timeout = setTimeout(() => {
          setCurrentText(currentFullText.slice(0, currentText.length + 1));
        }, 100);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setCurrentText('');
          setTextIndex((prev) => (prev + 1) % texts.length);
        }, 2000);
        return () => clearTimeout(timeout);
      }
    }
  }, [currentText, textIndex, isLoading, texts]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMenuOpen(false);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <div className="text-green-400 text-base font-mono animate-pulse">
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen font-mono">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-40 bg-black/95 backdrop-blur-md border-b border-green-500/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="text-lg font-bold text-green-400">
              &gt; portfolio.exe
            </div>
            
            <div className="hidden md:flex space-x-6">
              {[
                { name: 'about', id: 'about' },
                { name: 'projects', id: 'projects' },
                { name: 'skills', id: 'skills' },
                { name: 'contact', id: 'contact' }
              ].map((item) => (
                <button 
                  key={item.name} 
                  onClick={() => scrollToSection(item.id)}
                  className="hover:text-green-400 transition-colors duration-300 relative group cursor-pointer">
                  ./{item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-green-400 transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)} 
                className="text-white hover:text-green-400 transition-all duration-300 p-2">
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="bg-black/95 backdrop-blur-md border-t border-green-500/20">
              <div className="px-4 pt-2 pb-4 space-y-2">
                {[
                  { name: 'about', id: 'about' },
                  { name: 'projects', id: 'projects' },
                  { name: 'skills', id: 'skills' },
                  { name: 'contact', id: 'contact' }
                ].map((item, index) => (
                  <button 
                    key={item.name} 
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left px-3 py-2 hover:text-green-400 transition-all duration-300 text-base bg-gray-900/30 rounded border border-gray-700 hover:border-green-400/50 transform transition-transform ${
                      isMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                    style={{
                      transitionDelay: isMenuOpen ? `${index * 50}ms` : '0ms'
                    }}>
                    ./{item.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-4 pt-14">
        <div className="max-w-4xl w-full">
          <div className="mb-6 sm:mb-8 mt-4">
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ whoami</div>
            <div className="text-3xl sm:text-4xl md:text-6xl font-bold mb-3 sm:mb-4">
              Atom Gamer Arda A.G.A
            </div>
            <div className="text-lg sm:text-xl text-green-400 mb-3 sm:mb-4">
              Cybersecurity Specialist
            </div>
            <div className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-4 sm:mb-6 h-8">
              {currentText}<span className="animate-pulse">|</span>
            </div>
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ cat bio.txt</div>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-2xl">
              Full-stack developer specializing in modern web technologies and cybersecurity. 
              Experienced in vulnerability detection, penetration testing, and developing custom security tools.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
            <button 
              onClick={() => scrollToSection('projects')}
              className="flex items-center justify-center px-5 py-4 bg-green-600 hover:bg-green-500 active:bg-green-700 rounded transition-colors duration-300 text-sm sm:text-base touch-manipulation">
              <Terminal className="mr-2 w-4 h-4" />
              ./view_projects
            </button>
            <button 
              onClick={() => {
                // CV download functionality here
                console.log('CV download clicked');
              }}
              className="flex items-center justify-center px-5 py-4 border border-green-400 text-green-400 hover:bg-green-400 hover:text-black active:bg-green-500 active:text-black rounded transition-colors duration-300 text-sm sm:text-base touch-manipulation">
              <Download className="mr-2 w-4 h-4" />
              wget cv.pdf
            </button>
          </div>

          <div className="flex justify-center sm:justify-start space-x-6">
            <a href="https://github.com/ATOMGAMERAGA" target="_blank" rel="noopener noreferrer" className="touch-manipulation">
              <Github className="w-6 h-6 sm:w-6 sm:h-6 text-gray-400 hover:text-green-400 active:text-green-500 transition-colors cursor-pointer" />
            </a>
            <a href="https://www.linkedin.com/in/nuri-arda-ekici-b56791271" target="_blank" rel="noopener noreferrer" className="touch-manipulation">
              <Linkedin className="w-6 h-6 sm:w-6 sm:h-6 text-gray-400 hover:text-green-400 active:text-green-500 transition-colors cursor-pointer" />
            </a>
            <a href="mailto:info@atomland.xyz" className="touch-manipulation">
              <Mail className="w-6 h-6 sm:w-6 sm:h-6 text-gray-400 hover:text-green-400 active:text-green-500 transition-colors cursor-pointer" />
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ ls -la about/</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">About Me</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            <div>
              <div className="text-green-400 mb-3 sm:mb-4 text-sm sm:text-base">$ cat experience.txt</div>
              <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6 mb-4 sm:mb-6">
                <p className="text-gray-300 leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">
                  Full-stack developer and cybersecurity specialist with 5+ years of experience, developing 
                  secure and user-focused solutions using modern web technologies.
                </p>
                <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                  Deep expertise in penetration testing, vulnerability analysis, and modern JavaScript ecosystem.
                </p>
              </div>
            </div>

            <div>
              <div className="text-green-400 mb-3 sm:mb-4 text-sm sm:text-base">$ ./get_stats.sh</div>
              <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>Completed Projects:</span>
                    <span className="text-green-400">50+</span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>Lines of Code:</span>
                    <span className="text-green-400">100K+</span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>Coffee Consumed:</span>
                    <span className="text-green-400">∞</span>
                  </div>
                  <div className="flex justify-between text-sm sm:text-base">
                    <span>Years Experience:</span>
                    <span className="text-green-400">5+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900/20">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ cat /proc/skills</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Technical Skills</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6">
              <div className="text-green-400 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                <Code className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Frontend
              </div>
              <div className="space-y-2">
                {[
                  'React.js',
                  'Next.js', 
                  'TypeScript',
                  'Tailwind CSS',
                  'Vue.js'
                ].map((skill) => (
                  <div key={skill} className="flex items-center text-sm sm:text-base">
                    <span className="text-green-400 mr-2">></span>
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6">
              <div className="text-green-400 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                <Terminal className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Backend
              </div>
              <div className="space-y-2">
                {[
                  'Node.js',
                  'Express.js',
                  'Python',
                  'MongoDB',
                  'PostgreSQL'
                ].map((skill) => (
                  <div key={skill} className="flex items-center text-sm sm:text-base">
                    <span className="text-green-400 mr-2">></span>
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
              <div className="text-green-400 mb-3 sm:mb-4 flex items-center text-sm sm:text-base">
                <Shield className="mr-2 w-4 h-4 sm:w-5 sm:h-5" />
                Tools & Others
              </div>
              <div className="space-y-2">
                {[
                  'Git & GitHub',
                  'Docker',
                  'AWS',
                  'Figma',
                  'Linux'
                ].map((skill) => (
                  <div key={skill} className="flex items-center text-sm sm:text-base">
                    <span className="text-green-400 mr-2">></span>
                    <span className="text-gray-300">{skill}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ ls projects/</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Featured Projects</h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: 'AutoC',
                description: 'Otomatik C kodu generator ve geliştirici aracı - Kod üretim ve optimizasyon sistemi',
                tech: ['C', 'Automation', 'Code Generation', 'CLI'],
                status: 'Completed',
                link: 'https://github.com/ATOMGAMERAGA/autoc',
                isGithub: true
              },
              {
                title: 'Atomland Optimized', 
                description: 'Optimize edilmiş Atomland projesi - Performans odaklı web uygulaması',
                tech: ['React', 'Optimization', 'Performance', 'Web'],
                status: 'Aktif',
                link: 'https://github.com/ATOMGAMERAGA/atomland-optimized',
                isGithub: true
              },
              {
                title: 'ATOMLAND Optimizer Plugin',
                description: 'Minecraft için performans artırıcı optimizasyon plugini - Server performansı geliştirici',
                tech: ['Java', 'Minecraft', 'Plugin', 'Optimization', 'Spigot'],
                status: 'Aktif',
                link: 'https://dn.atomland.xyz/job/ATOMLAND-Optimizer/',
                isGithub: false
              }
            ].map((project, index) => (
              <div key={index} className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6 hover:border-green-400/50 transition-colors duration-300">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-lg sm:text-xl font-bold text-green-400">{project.title}</h3>
                  <span className={`px-2 py-1 text-xs rounded font-semibold ${
                    project.status === 'Aktif' ? 'bg-green-600 text-white animate-pulse' : 'bg-gray-600 text-gray-300'
                  }`}>
                    {project.status}
                  </span>
                </div>
                <p className="text-gray-300 mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
                  {project.tech.map((tech) => (
                    <span key={tech} className="px-2 py-1 bg-gray-800 text-green-400 text-xs sm:text-sm rounded border border-gray-600">
                      {tech}
                    </span>
                  ))}
                </div>
                <div className="flex space-x-4">
                  <a 
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-green-400 hover:text-white active:text-green-300 transition-colors text-xs sm:text-sm touch-manipulation">
                    <ExternalLink className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
                    {project.isGithub ? 'GitHub' : 'Demo'}
                  </a>
                  {project.isGithub && (
                    <a 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-gray-400 hover:text-white active:text-gray-300 transition-colors text-xs sm:text-sm touch-manipulation">
                      <Github className="mr-1 w-3 h-3 sm:w-4 sm:h-4" />
                      Code
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <div className="text-green-400 mb-2 text-sm sm:text-base">$ ./contact.sh</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Get In Touch</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
            <div>
              <div className="text-green-400 mb-3 sm:mb-4 text-sm sm:text-base">$ cat contact_info.txt</div>
              <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center">
                    <Mail className="mr-3 w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    <span className="text-gray-300 text-sm sm:text-base">info@atomland.xyz</span>
                  </div>
                  <div className="flex items-center">
                    <Linkedin className="mr-3 w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    <span className="text-gray-300 text-sm sm:text-base">linkedin.com/in/nuri-arda-ekici</span>
                  </div>
                  <div className="flex items-center">
                    <Github className="mr-3 w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                    <span className="text-gray-300 text-sm sm:text-base">github.com/ATOMGAMERAGA</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="text-green-400 mb-3 sm:mb-4 text-sm sm:text-base">$ nano message.txt</div>
              <div className="bg-gray-900/50 border border-gray-700 rounded p-4 sm:p-6">
                <p className="text-gray-300 leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">
                  Feel free to reach out for new projects and collaboration opportunities. 
                  Always open to facing new challenges and learning.
                </p>
                <button 
                  onClick={() => {
                    // Contact form or mailto functionality
                    window.location.href = 'mailto:info@atomland.xyz?subject=Project Inquiry&body=Hello, I would like to discuss a project opportunity.';
                  }}
                  className="flex items-center justify-center w-full sm:w-auto px-5 py-4 bg-green-600 hover:bg-green-500 active:bg-green-700 rounded transition-colors duration-300 text-sm sm:text-base touch-manipulation">
                  <Mail className="mr-2 w-4 h-4" />
                  ./send_message
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terminal Footer */}
      <footer className="py-6 sm:py-8 px-4 border-t border-gray-700">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex space-x-6">
              <a href="https://github.com/ATOMGAMERAGA" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-400 active:text-green-500 transition-colors touch-manipulation p-2">
                <Github className="w-6 h-6" />
              </a>
              <a href="https://www.linkedin.com/in/nuri-arda-ekici-b56791271" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-400 active:text-green-500 transition-colors touch-manipulation p-2">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href="mailto:info@atomland.xyz" className="text-gray-400 hover:text-green-400 active:text-green-500 transition-colors touch-manipulation p-2">
                <Mail className="w-6 h-6" />
              </a>
              <a href="https://dc.atomland.xyz" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-green-400 active:text-green-500 transition-colors touch-manipulation p-2">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </a>
            </div>
            <div className="text-center">
              <div className="text-green-400 mb-1 text-sm">$ uptime</div>
              <p className="text-gray-400 text-xs sm:text-sm mb-1">
                © 2025 ATOMLAND • Built by Atom Gamer Arda
              </p>
              <p className="text-gray-400 text-xs sm:text-sm">
                <a 
                  href="https://status.atomland.xyz" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-400 hover:text-green-300 transition-colors underline decoration-dotted"
                >
                  System uptime: 99.9%
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ModernPortfolio;
